/**
 * Marker management page - view all markers, reassign, print, and generate new.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBoxStore } from '../store/BoxContext';
import { useToast } from '../store/ToastContext';
import { BoxListSkeleton } from '../components/Skeleton';
import { EmptyState } from '../components/EmptyState';
import { ReassignMarkerModal } from '../components/ReassignMarkerModal';
import { NewMarkerModal } from '../components/NewMarkerModal';
import { getArucoSvgUrl, getQrSvgUrl, getLabelPdfUrl } from '../api/markers';
import type { Box } from '@shared/types';

type MarkerFilter = 'all' | 'aruco' | 'qr';

export function MarkersPage() {
  const navigate = useNavigate();
  const { state, fetchBoxes } = useBoxStore();
  const { addToast } = useToast();
  const [filter, setFilter] = useState<MarkerFilter>('all');
  const [reassignModal, setReassignModal] = useState<Box | null>(null);
  const [newMarkerModalOpen, setNewMarkerModalOpen] = useState(false);

  useEffect(() => {
    fetchBoxes({ sortBy: 'markerType', sortOrder: 'asc' }).catch(() => {
      addToast('error', 'Failed to load markers. Please try again.');
    });
  }, [fetchBoxes, addToast]);

  const filteredBoxes = state.boxes.filter((box) => {
    if (filter === 'all') return true;
    return box.markerType === filter;
  });

  function handlePrintMarker(box: Box) {
    const pdfUrl = getLabelPdfUrl(box.markerType, box.markerId);
    window.open(pdfUrl, '_blank');
    addToast('success', 'Opening marker label PDF');
  }

  function getMarkerPreviewUrl(box: Box, size = 48): string {
    if (box.markerType === 'aruco') {
      return getArucoSvgUrl(parseInt(box.markerId, 10), size);
    }
    return getQrSvgUrl(box.markerId, size);
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-text-primary">Markers</h1>
        <button
          onClick={() => setNewMarkerModalOpen(true)}
          className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
        >
          Generate New Marker
        </button>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        <button
          onClick={() => setFilter('all')}
          className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
            filter === 'all'
              ? 'bg-accent text-white'
              : 'bg-surface text-text-secondary hover:bg-surface-hover'
          }`}
        >
          All ({state.boxes.length})
        </button>
        <button
          onClick={() => setFilter('aruco')}
          className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
            filter === 'aruco'
              ? 'bg-accent text-white'
              : 'bg-surface text-text-secondary hover:bg-surface-hover'
          }`}
        >
          ArUco ({state.boxes.filter((b) => b.markerType === 'aruco').length})
        </button>
        <button
          onClick={() => setFilter('qr')}
          className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
            filter === 'qr'
              ? 'bg-accent text-white'
              : 'bg-surface text-text-secondary hover:bg-surface-hover'
          }`}
        >
          QR ({state.boxes.filter((b) => b.markerType === 'qr').length})
        </button>
      </div>

      {/* Loading */}
      {state.loading && state.boxes.length === 0 && <BoxListSkeleton />}

      {/* Empty State */}
      {!state.loading && filteredBoxes.length === 0 && (
        <EmptyState
          title="No markers found"
          description={
            filter === 'all'
              ? 'Create your first box with a marker to get started.'
              : `No ${filter} markers found. Try a different filter.`
          }
        />
      )}

      {/* Marker List - Table on desktop, cards on mobile */}
      {!state.loading && filteredBoxes.length > 0 && (
        <>
          {/* Desktop Table */}
          <div className="hidden overflow-hidden rounded-lg border border-border md:block">
            <table className="w-full">
              <thead className="bg-surface-hover">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">
                    Preview
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">
                    Marker ID
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">
                    Box Name
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-text-secondary">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-surface">
                {filteredBoxes.map((box) => (
                  <tr key={box.id} className="hover:bg-surface-hover">
                    <td className="px-4 py-3">
                      <img
                        src={getMarkerPreviewUrl(box)}
                        alt={`${box.markerType} ${box.markerId}`}
                        className="h-12 w-12 rounded border border-border"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          box.markerType === 'aruco'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                            : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        }`}
                      >
                        {box.markerType.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-sm text-text-primary">
                      {box.markerId}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-primary">{box.name}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => navigate(`/boxes/${box.id}`)}
                          className="text-sm text-accent hover:text-accent-hover"
                        >
                          View
                        </button>
                        <button
                          onClick={() => handlePrintMarker(box)}
                          className="text-sm text-accent hover:text-accent-hover"
                        >
                          Print
                        </button>
                        <button
                          onClick={() => setReassignModal(box)}
                          className="text-sm text-accent hover:text-accent-hover"
                        >
                          Reassign
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="grid gap-3 md:hidden">
            {filteredBoxes.map((box) => (
              <div key={box.id} className="rounded-lg border border-border bg-surface p-4">
                <div className="flex items-start gap-3">
                  <img
                    src={getMarkerPreviewUrl(box, 64)}
                    alt={`${box.markerType} ${box.markerId}`}
                    className="h-16 w-16 flex-shrink-0 rounded border border-border"
                  />
                  <div className="flex-1 space-y-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium text-text-primary">{box.name}</h3>
                        <p className="mt-1 font-mono text-xs text-text-secondary">
                          {box.markerType.toUpperCase()}: {box.markerId}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => navigate(`/boxes/${box.id}`)}
                        className="flex-1 rounded-lg border border-border bg-surface px-3 py-1.5 text-sm text-text-primary hover:bg-surface-hover"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handlePrintMarker(box)}
                        className="flex-1 rounded-lg border border-border bg-surface px-3 py-1.5 text-sm text-text-primary hover:bg-surface-hover"
                      >
                        Print
                      </button>
                      <button
                        onClick={() => setReassignModal(box)}
                        className="flex-1 rounded-lg bg-accent px-3 py-1.5 text-sm text-white hover:bg-accent-hover"
                      >
                        Reassign
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Modals */}
      {reassignModal && (
        <ReassignMarkerModal box={reassignModal} onClose={() => setReassignModal(null)} />
      )}
      {newMarkerModalOpen && <NewMarkerModal onClose={() => setNewMarkerModalOpen(false)} />}
    </div>
  );
}
